import React, { useEffect } from 'react';
import SEO from '../SEO';
import seoData from '../../seoData';
import { Wrench, Heart, Users, Flag } from 'lucide-react';
import Header from '../Header';
import Footer from '../Footer';
import PageHero from '../ui/PageHero';

const MissionStatement = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const pillars = [
    {
      icon: Wrench,
      title: 'Innovative Repair & Restoration',
      description: 'Extending the life of critical infrastructure and industrial equipment through engineered repair, restoration, and protective solutions.'
    },
    {
      icon: Heart,
      title: 'Integrity',
      description: 'Conducting our business honestly, transparently, and in a way that earns lasting trust with every customer we serve.'
    },
    {
      icon: Users,
      title: 'Excellence in Service',
      description: 'Meeting every project with craftsmanship, discipline, and a commitment to doing the job right the first time.'
    },
    {
      icon: Flag,
      title: 'God, Family & Country',
      description: 'Guided by faith, grounded in family, and proud to serve the industries and communities that keep America running.'
    }
  ];

  return (
    <div className="min-h-screen bg-white" data-testid="mission-statement-page">
      <Header />
      <SEO
        title={seoData['about/mission-statement']?.title}
        description={seoData['about/mission-statement']?.description}
        path="/about/mission-statement"
        keywords={seoData['about/mission-statement']?.keywords || []}
      />

      <PageHero
        badge="Our Mission"
        title="Mission Statement"
        description="What drives BCS — and the principles behind every project we take on."
        pageKey="mission-statement"
        showButton={false}
      />

      {/* Mission Statement — hero quote */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div
            className="relative rounded-2xl bg-gradient-to-br from-blue-900 to-slate-900 text-white p-10 sm:p-14 shadow-xl"
            data-testid="mission-statement-quote"
          >
            {/* Decorative quote mark */}
            <div className="absolute top-6 left-8 text-6xl leading-none text-orange-400/70 font-serif select-none">
              &ldquo;
            </div>

            <div className="pl-8 sm:pl-10">
              <p className="text-lg sm:text-xl leading-relaxed text-slate-100 mb-6">
                Our mission is to extend the life of critical infrastructure and industrial
                equipment through innovative repair, restoration, and protective solutions.
              </p>
              <p className="text-lg sm:text-xl leading-relaxed text-slate-100 mb-6">
                We are committed to conducting our business with integrity, serving our
                customers with excellence, and remaining guided by our commitment to
                <span className="text-orange-400 font-semibold"> God, Family, and Country.</span>
              </p>

              <div className="pt-6 mt-6 border-t border-white/15">
                <div className="text-white font-bold text-lg">Jason M. Kraft</div>
                <div className="text-orange-400 text-sm font-medium tracking-wide">President &amp; Owner</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pillars grid */}
      <section className="pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="!text-3xl !font-bold text-slate-900 mb-3">The Principles Behind Our Work</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Four commitments that guide how we plan, execute, and stand behind every job.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {pillars.map((pillar, index) => {
              const Icon = pillar.icon;
              return (
                <div
                  key={index}
                  data-testid={`mission-pillar-${index}`}
                  className="group p-8 rounded-xl border border-slate-200 bg-white hover:border-blue-400 hover:shadow-lg transition-all duration-300"
                >
                  <div className="w-12 h-12 rounded-lg bg-blue-50 group-hover:bg-blue-100 flex items-center justify-center mb-5 transition-colors">
                    <Icon className="w-6 h-6 text-blue-900" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-2">{pillar.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{pillar.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default MissionStatement;
